/**
 * `openDocPdf` — open (or download) a server-rendered document PDF (ADR-0021).
 *
 * The PDF endpoints live on Hono (`/api/<doc>/:id/pdf`), not the SPA, so we
 * use the absolute API origin to bypass the Vite SPA fallback. The session
 * cookie authenticates the request — same pattern as the invoice/bill print
 * helpers, lifted here so every detail page shares one implementation.
 *
 * Fetches the response first (rather than a raw `window.open`) so a failure
 * — Gotenberg unreachable, template missing, tenant scope violation — surfaces
 * as a friendly `toast.error(...)` instead of raw JSON in a blank tab. On
 * success we open (or download) a `Blob:` URL so the browser still renders the
 * PDF natively. @see issue #135 — the Gotenberg-unreachable UX bug this fixes.
 *
 * On the VIEW path we open a placeholder tab **synchronously** inside the
 * click handler and swap its `.location` to the blob URL after the fetch
 * resolves. Doing `window.open(blobUrl, …)` after `await fetch(...)` burns the
 * user-activation and gets blocked by Safari (always) and Chrome/Firefox (slow
 * networks). @see PR #140 review — the popup-blocker regression this closes.
 *
 * Trade-off: holding the placeholder window handle disables `noopener` for the
 * view path. Acceptable here — we're loading our own auth-scoped blob into
 * that tab, no untrusted content. The DOWNLOAD path still uses an anchor with
 * `rel="noopener"`.
 *
 * @example openDocPdf(`/api/receipts/${id}/pdf`, { download: true, filename: `receipt-${id}` })
 */
import { toast } from "@flint/ui";
import { WEB_ENV } from "./env.ts";

interface OpenDocPdfOptions {
  download?: boolean;
  filename?: string;
}

interface PdfErrorBody {
  error?: string;
  detail?: string;
}

function friendlyErrorMessage(body: PdfErrorBody, status: number): string {
  // Adapter-shaped errors from doc-pdf.ts. `render-failed` covers every
  // Gotenberg unreachable / template render explosion — the print service
  // is down, telling the user the raw `fetch failed` doesn't help them.
  if (body.error === "render-failed") {
    return "The print service is temporarily unavailable. Please try again in a moment.";
  }
  if (body.error === "template-missing") {
    return "The document template hasn't been set up yet. Contact support.";
  }
  if (status === 401 || status === 403) {
    return "You don't have permission to print this document.";
  }
  if (status === 404) {
    return "The document couldn't be found.";
  }
  return body.detail ?? body.error ?? `Request failed (HTTP ${status}).`;
}

/**
 * Read a filename from the response's `Content-Disposition` header, so a
 * download without an explicit `opts.filename` doesn't fall back to a
 * random-UUID blob-URL filename. RFC 5987 (`filename*=UTF-8''…`) first, then
 * simple `filename="…"` / `filename=…`. Returns null if the header is missing
 * or unparsable.
 */
function filenameFromContentDisposition(res: Response): string | null {
  const cd = res.headers.get("Content-Disposition");
  if (!cd) return null;
  const rfc5987 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cd);
  if (rfc5987 && rfc5987[1]) {
    try {
      return decodeURIComponent(rfc5987[1]).trim();
    } catch {
      // Malformed percent-encoding — fall through to the simple form.
    }
  }
  const simple = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(cd);
  const raw = simple?.[1] ?? simple?.[2];
  return raw ? raw.trim() : null;
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function openDocPdf(
  path: string,
  opts: OpenDocPdfOptions = {},
): Promise<void> {
  const url = `${WEB_ENV.VITE_FLINT_API_URL}${path}${opts.download ? "?download=1" : ""}`;

  // View path: open a placeholder tab SYNCHRONOUSLY so the popup blocker
  // respects the still-live user activation from the click. We swap its
  // `.location` to the blob URL after the fetch. `null` means the browser
  // blocked even the synchronous open — extreme edge, handled at the end.
  const viewTab: Window | null = opts.download
    ? null
    : window.open("", "_blank");

  let res: Response;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch {
    // Network-class failure — SPA can't reach the API at all. Close the
    // placeholder tab so the user isn't left with an orphan blank tab.
    viewTab?.close();
    toast.error("Couldn't generate the PDF", {
      description: "Check your connection and try again.",
    });
    return;
  }

  if (!res.ok) {
    viewTab?.close();
    const body: PdfErrorBody = await res.json().catch(() => ({}));
    toast.error("Couldn't generate the PDF", {
      description: friendlyErrorMessage(body, res.status),
    });
    return;
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);

  if (opts.download) {
    // Prefer the caller's filename; fall back to `Content-Disposition`; final
    // fallback is a generic name (better than a random-UUID download).
    const filename =
      opts.filename ?? filenameFromContentDisposition(res) ?? "document.pdf";
    triggerDownload(blobUrl, filename);
    // Anchor click has fired; revoke after a delay to free the blob.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  if (viewTab) {
    viewTab.location.href = blobUrl;
    // Deliberately do NOT revoke — the tab keeps needing the blob URL for
    // the life of the session (reload, back-forward navigation). The blob
    // dies naturally when the tab closes.
    return;
  }

  // Extreme edge: even the synchronous pre-open was blocked (aggressive user
  // settings). Same-tab navigation is better than silently doing nothing.
  window.location.href = blobUrl;
}
